import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios'

import {
  nativeRefreshBody,
  persistRotatedRefresh,
  refreshTokenStore,
} from '@/features/auth/lib/refreshTokenStore'
import { useAuthStore } from '@/features/auth/store/authStore'
import { useLocaleStore } from '@/shared/store/localeStore'
import { useUpdateRequiredStore } from '@/shared/store/updateRequiredStore'

import { normalizeApiError } from './ApiError'
import { appVersion } from './appVersion'
import { clientPlatform } from './platform'

type RetryableConfig = InternalAxiosRequestConfig & { _retry?: boolean }

const AUTH_PATHS = ['/auth/refresh', '/auth/login', '/auth/register', '/auth/logout']

function isAuthEndpoint(url?: string): boolean {
  if (!url) return false
  return AUTH_PATHS.some((p) => url.includes(p))
}

export const httpClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  withCredentials: true,
})

httpClient.interceptors.request.use(async (config) => {
  const { accessToken } = useAuthStore.getState()
  if (accessToken) {
    config.headers.set('Authorization', `Bearer ${accessToken}`)
  }
  config.headers.set('Accept-Language', useLocaleStore.getState().locale)
  // Cliente nativo (Capacitor): el backend usa este header para emitir/aceptar el
  // refresh token por body (ver docs/specs/2026-09-03-auth-refresh-nativo.md).
  const platform = clientPlatform()
  if (platform) {
    config.headers.set('X-Client-Platform', platform)
    // Versión de la app de tienda: versión mínima + medir versiones en uso (spec
    // 2026-10-17-version-minima-app). El backend la tiene en CORS desde antes de la 1.0.7.
    const version = await appVersion()
    if (version) config.headers.set('X-App-Version', version)
  }
  return config
})

/** Código del 426 con el que el backend pide actualizar la app (`AppVersionFilter`). */
const UPDATE_REQUIRED = 'error.app.update.required'

interface RefreshResponseBody {
  accessToken: string
  accessTokenExpiresAt: string
  /** Refresh rotado en el body (solo nativo). */
  refreshToken?: string
}

let pendingRefresh: Promise<RefreshResponseBody | null> | null = null

async function performRefresh(): Promise<RefreshResponseBody | null> {
  pendingRefresh ??= (async () => {
    try {
      // Nativo: manda el refresh token guardado en el body y persiste el rotado.
      // Web: body undefined → rota por cookie, como siempre.
      const body = await nativeRefreshBody()
      const { data } = await httpClient.post<RefreshResponseBody>('/auth/refresh', body)
      await persistRotatedRefresh(data.refreshToken)
      return data
    } catch {
      return null
    } finally {
      pendingRefresh = null
    }
  })()
  return pendingRefresh
}

async function handleAuthFailure(originalError: AxiosError): Promise<never> {
  const { useAuthStore: store } = await import('@/features/auth/store/authStore')
  const { refreshScheduler } = await import('@/features/auth/lib/refreshScheduler')
  const { authBroadcast } = await import('@/features/auth/lib/authBroadcast')
  store.getState().clear()
  await refreshTokenStore.clear()
  refreshScheduler.cancel()
  authBroadcast.publish('logout')
  if (typeof window !== 'undefined') {
    const from = encodeURIComponent(window.location.pathname + window.location.search)
    window.location.assign(`/login?reason=session-expired&from=${from}`)
  }
  throw normalizeApiError(originalError)
}

httpClient.interceptors.response.use(
  (r) => r,
  async (error: AxiosError) => {
    const original = error.config as RetryableConfig | undefined
    if (
      error.response?.status === 426 &&
      (error.response.data as { code?: unknown } | undefined)?.code === UPDATE_REQUIRED
    ) {
      useUpdateRequiredStore.getState().markRequired()
    }
    if (
      error.response?.status === 401 &&
      original &&
      !original._retry &&
      !isAuthEndpoint(original.url)
    ) {
      original._retry = true
      const data = await performRefresh()
      if (data) {
        const { refreshScheduler } = await import('@/features/auth/lib/refreshScheduler')
        useAuthStore
          .getState()
          .setAccessToken(data.accessToken, Date.parse(data.accessTokenExpiresAt))
        refreshScheduler.schedule()
        original.headers.set('Authorization', `Bearer ${data.accessToken}`)
        return httpClient(original)
      }
      return handleAuthFailure(error)
    }
    return Promise.reject(normalizeApiError(error))
  },
)
